import { getChangedRanges } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

export type TInvisibleCharactersState = {
  visible: boolean;
  decorations: DecorationSet;
};

export const INVISIBLE_CHARACTERS_PLUGIN_KEY = new PluginKey<TInvisibleCharactersState>('invisibleCharacters');

export const SET_INVISIBLE_CHARACTERS_VISIBLE_META = 'setInvisibleCharactersVisible';

type TWidgetType = 'space' | 'paragraph' | 'break';

const createWidget = (pos: number, type: TWidgetType): Decoration =>
  Decoration.widget(
    pos,
    () => {
      const span = document.createElement('span');
      span.classList.add('tiptap-invisible-character', `tiptap-invisible-character--${type}`);
      return span;
    },
    { key: type, marks: [], side: 1000 }
  );

const collectWidgets = (doc: ProseMirrorNode, from: number, to: number): Decoration[] => {
  const widgets: Decoration[] = [];
  doc.nodesBetween(from, to, (node, pos) => {
    if (node.isText && node.text) {
      const start = Math.max(from, pos) - pos;
      const end = Math.min(to, pos + node.text.length) - pos;
      for (let i = start; i < end; i++) {
        if (node.text[i] === ' ') widgets.push(createWidget(pos + i, 'space'));
      }
    } else if (node.type.name === 'paragraph') {
      widgets.push(createWidget(pos + node.nodeSize - 1, 'paragraph'));
    } else if (node.type.name === 'hardBreak') {
      widgets.push(createWidget(pos, 'break'));
    }
  });
  return widgets;
};

const buildAll = (doc: ProseMirrorNode): DecorationSet =>
  DecorationSet.create(doc, collectWidgets(doc, 0, doc.content.size));

// Builds nothing until the first toggle to visible; while hidden every transaction is a no-op.
// The vendor plugin instead builds a widget per space for the whole doc up front - most of the load cost on large documents.
export const getInvisibleCharactersPlugin = (initialVisible: boolean): Plugin<TInvisibleCharactersState> =>
  new Plugin<TInvisibleCharactersState>({
    key: INVISIBLE_CHARACTERS_PLUGIN_KEY,

    state: {
      init: (_config, state) => ({
        visible: initialVisible,
        decorations: initialVisible ? buildAll(state.doc) : DecorationSet.empty,
      }),

      apply: (tr, prev) => {
        const metaVisible = tr.getMeta(SET_INVISIBLE_CHARACTERS_VISIBLE_META) as boolean | undefined;
        const visible = metaVisible ?? prev.visible;

        // Hidden: never build or map anything.
        if (!visible) return prev.visible ? { visible: false, decorations: DecorationSet.empty } : prev;
        // Just toggled on: one full build.
        if (!prev.visible) return { visible, decorations: buildAll(tr.doc) };
        if (!tr.docChanged) return prev;

        // Mapping keeps widgets on surviving content and drops them in deleted ranges, so only changed ranges need rebuilding.
        let decorations = prev.decorations.map(tr.mapping, tr.doc);
        for (const {
          newRange: { from, to },
        } of getChangedRanges(tr)) {
          const widgets = collectWidgets(tr.doc, from, to);
          const stale = [
            // Rebuild the window interior; a widget exactly at `to` was placed by the mapping, outside the re-collected chars, so it stays.
            ...decorations.find(from, to).filter((decoration) => decoration.from < to),
            // A re-collected widget can also sit outside the window (a paragraph-end mark while typing inside it) - replace per position, don't duplicate.
            ...widgets.flatMap((widget) => decorations.find(widget.from, widget.from)),
          ];
          decorations = decorations.remove(stale).add(tr.doc, widgets);
        }
        return { visible, decorations };
      },
    },

    props: {
      decorations(state) {
        return INVISIBLE_CHARACTERS_PLUGIN_KEY.getState(state)?.decorations;
      },
    },
  });
